<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Slim top rule -->
      <div class="top-rule">
        <div class="tr-fill lo-fill"></div>
        <div class="tr-fill re-fill"></div>
      </div>

      <!-- Nav -->
      <nav class="ln-nav">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="ln-logo" />
        <div class="ln-nav-links">
          <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="ln-nav-link">{{ loinfo.phone_number }}</a>
         
        </div>
      </nav>

      <!-- Full width hero band -->
      <section class="ln-hero-band">
        <div class="lhb-inner">
          <div class="lhb-text">
            <span class="lhb-kicker">Mortgage Partnership</span>
            <h1 class="lhb-h1" v-html="heroH1Html"></h1>
            <p class="lhb-body">{{ heroBody }}</p>
            <div class="lhb-btns">
              <a class="lhb-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="lhb-btn-soft" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
          </div>
          <div class="lhb-decor" aria-hidden="true">
            <div class="ld-circle ld-c1"></div>
            <div class="ld-circle ld-c2"></div>
            <div class="ld-circle ld-c3"></div>
          </div>
        </div>
      </section>

      <!-- Agents band -->
      <section class="ln-agents">
        <div class="la-inner">

          <div class="ln-profile lo-lp">
            <div class="lp-photo-wrap">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="lp-photo" />
              <div class="lp-photo-frame lo-frame"></div>
            </div>
            <div class="lp-text">
              <div class="lp-overtitle">Loan Officer</div>
              <div class="lp-name">{{ loName }}</div>
              <div class="lp-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="lp-nmls">NMLS # {{ (loinfo.nmls || "").trim() }}</div>
              <div class="lp-links">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="lp-link lo-lp-link">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="lp-link lo-lp-link">{{ loinfo.email }}</a>
              </div>
       
            </div>
          </div>

          <div class="la-divider">
            <div class="lad-line"></div>
            <div class="lad-dot"></div>
            <div class="lad-line"></div>
          </div>

          <div class="ln-profile re-lp">
            <div class="lp-photo-wrap">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="lp-photo" />
              <div class="lp-photo-frame re-frame"></div>
            </div>
            <div class="lp-text">
              <div class="lp-overtitle re-overtitle">Partner</div>
              <div class="lp-name">{{ realtorName }}</div>
              <div class="lp-role">{{ realtor && realtor.brokerage_name }}</div>
              <div class="lp-links">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="lp-link re-lp-link">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="lp-link re-lp-link">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="lp-link re-lp-link" target="_blank" rel="noreferrer">Visit Website →</a>
              </div>
            </div>
          </div>

        </div>
      </section>

      <!-- Footer -->
      <footer class="ln-footer">
        <span>{{ loName }} · {{ loTitle }}</span>
        <div class="lf-spacer"></div>
        <span v-if="loinfo && loinfo.nmls" class="lf-right">NMLS {{ (loinfo.nmls || "").trim() }}</span>
      </footer>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandLinen",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "A home that<br/><em>feels just right.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Karla:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #fdf9f4;
  color: #1e1a14;
  font-family: 'Karla', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #fdf9f4;
  --warm: #c8b89a;
  --card-bg: #fff;
  --border: rgba(30,26,20,0.09);
  --muted: #7a7060;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* TOP RULE */
.top-rule { height: 4px; display: flex; }
.tr-fill { flex: 1; }
.lo-fill { background: var(--p); }
.re-fill { background: var(--a); }

/* NAV */
.ln-nav { display: flex; align-items: center; justify-content: space-between; padding: 22px 60px; border-bottom: 1px solid var(--border); }
.ln-logo { width: 148px; max-height:74px; object-fit: contain; }
.ln-nav-links { display: flex; align-items: center; gap: 24px; }
.ln-nav-link { font-size: 12px; color: var(--muted); text-decoration: none; transition: color 0.18s; }
.ln-nav-link:hover { color: var(--p); }
.ln-cta { font-size: 12px; font-weight: 600; text-decoration: none; background: var(--p); color: #fff; padding: 10px 22px; border-radius: 6px; transition: opacity 0.2s; }
.ln-cta:hover { opacity: 0.88; }

/* HERO BAND */
.ln-hero-band { background: var(--bg); padding: 72px 60px 52px; border-bottom: 1px solid var(--border); }
.lhb-inner { max-width: 1100px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 60px; }
.lhb-text { max-width: 560px; }
.lhb-kicker { display: inline-block; font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 18px; }
.lhb-h1 { font-family: 'Playfair Display', serif; font-size: clamp(36px, 5vw, 62px); font-weight: 700; line-height: 1.1; color: var(--p); margin: 0 0 20px; }
.lhb-h1 em { font-style: italic; font-weight: 400; color: color-mix(in srgb, var(--a) 70%, var(--p)); }
.lhb-body { font-size: 15px; line-height: 1.78; color: var(--muted); margin-bottom: 32px; font-weight: 300; }
.lhb-btns { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.lhb-btn { width:55%; font-size: 13px; font-weight: 600; text-decoration: none; background: var(--p); color: #fff; padding: 13px 30px; border-radius: 6px; transition: opacity 0.2s; }
.lhb-btn:hover { opacity: 0.88; }
.lhb-btn-soft { font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1.5px solid var(--border); padding: 13px 24px; border-radius: 6px; transition: background 0.18s; }
.lhb-btn-soft:hover { background: rgba(255,255,255,0.8); }
.lhb-decor { flex-shrink: 0; position: relative; width: 200px; height: 200px; }
.ld-circle { position: absolute; border-radius: 50%; }
.ld-c1 { width: 180px; height: 180px; border: 1.5px solid var(--border); top: 0; left: 0; }
.ld-c2 { width: 120px; height: 120px; border: 1.5px solid color-mix(in srgb, var(--a) 20%, transparent); top: 30px; left: 30px; }
.ld-c3 { width: 60px; height: 60px; background: color-mix(in srgb, var(--a) 10%, transparent); top: 60px; left: 60px; }

/* AGENTS */
.ln-agents { padding: 56px 60px; background: var(--card-bg); border-bottom: 1px solid var(--border); }
.la-inner { max-width: 900px; margin: 0 auto; display: flex; align-items: center; gap: 0; }
.ln-profile { flex: 1; display: flex; gap: 24px; align-items: flex-start; }
.lp-photo-wrap { position: relative; flex-shrink: 0; }
.lp-photo { width: 80px; height: 80px; border-radius: 12px; object-fit: cover; display: block; position: relative; z-index: 1; }
.lp-photo-frame { position: absolute; width: 80px; height: 80px; border-radius: 12px; border: 2px solid; top: 6px; left: 6px; z-index: 0; }
.lo-frame { border-color: color-mix(in srgb, var(--p) 15%, transparent); }
.re-frame { border-color: color-mix(in srgb, var(--a) 25%, transparent); }
.lp-overtitle { font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--p); margin-bottom: 4px; }
.re-overtitle { color: color-mix(in srgb, var(--a) 75%, #1e1a14); }
.lp-name { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: var(--p); line-height: 1.2; }
.lp-role { font-size: 12px; color: var(--muted); margin-top: 2px; }
.lp-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-top: 2px; }
.lp-links { display: flex; flex-direction: column; gap: 4px; margin: 12px 0; }
.lp-link { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.lo-lp-link:hover { color: var(--p); }
.re-lp-link:hover { color: color-mix(in srgb, var(--a) 75%, #1e1a14); }
.lp-cta { display: inline-block; font-size: 12px; font-weight: 600; text-decoration: none; background: var(--p); color: #fff; padding: 10px 22px; border-radius: 6px; transition: opacity 0.2s; }
.lp-cta:hover { opacity: 0.88; }
.la-divider { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 0 40px; align-self: stretch; }
.lad-line { flex: 1; width: 1px; background: var(--border); }
.lad-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--a); opacity: 0.5; }

/* FOOTER */
.ln-footer { display: flex; align-items: center; padding: 16px 60px; }
.ln-footer span { font-size: 11px; color: var(--muted); }
.lf-spacer { flex: 1; }
.lf-right { font-size: 11px; color: color-mix(in srgb, var(--muted) 60%, transparent); }

@media (max-width: 820px) {
  .ln-nav { padding: 16px 24px; }
  .ln-hero-band { padding: 48px 24px 36px; }
  .lhb-inner { flex-direction: column; gap: 32px; }
  .lhb-decor { display: none; }
  .ln-agents { padding: 36px 24px; }
  .la-inner { flex-direction: column; gap: 24px; }
  .la-divider { flex-direction: row; padding: 0; align-self: auto; width: 100%; }
  .lad-line { height: 1px; width: auto; }
  .ln-footer { padding: 14px 24px; }
}
</style>
